import { applyClientPositionPricing, clientPositionSelection, hasPendingClientRate } from './clientRequestPricing';
import { draftClientCellDecision, clientRequestWorksheetRows } from './updateQuote/clientRequestWorksheetRows';
type Row = Record<string, any>;
const fields = ['startDate', 'endDate', 'position', 'quantity', 'startTime', 'endTime', 'notes', 'rateType', 'callType', 'mealBreak', 'rate', 'registerHours', 'overTimeHours', 'DoubleTimeHours', 'addOns', 'miscFees'];
const normalized = (field: string, value: any) => field.endsWith('Date') ? String(value || '').split('T')[0] : value;
export function documentComparisons(original: Row[], requested: Row[]) {
  return [...original.map(row => {
    const proposal = requested.find(item => String(item.id) === String(row.id));
    const changes = proposal ? Object.fromEntries(fields.filter(field => normalized(field, row[field]) !== normalized(field, proposal[field]))
      .map(field => [field, { base: row[field], requested: proposal[field] }])) : {};
    return { shiftNumber: row.shiftNumber, status: !proposal ? 'deleted' : Object.keys(changes).length ? 'modified' : 'unchanged',
      originalData: row, historyData: proposal ? { ...proposal, id: proposal.clientRequestHistoryShiftId ?? proposal.id } : undefined, changes } as any;
  }), ...requested.filter(row => !original.some(item => String(item.id) === String(row.id))).map(row => ({ shiftNumber: row.shiftNumber,
    status: 'new', changes: {}, historyData: { ...row, id: row.clientRequestHistoryShiftId ?? row.id } } as any))];
}
export function reviewDocumentFields(row: Row, comparison: any, requestedFields: string[], decision: 'accept' | 'reject') {
  const changed = requestedFields.filter(field => comparison?.changes?.[field]);
  const patch = Object.fromEntries(changed.map(field => [field, normalized(field, comparison.changes[field][decision === 'accept' ? 'requested' : 'base'])]));
  const next = applyClientPositionPricing(row, comparison?.historyData, patch);
  if (decision === 'reject' && changed.includes('position') && row.clientRatePending) {
    next.rate = comparison.originalData.rate; next.totals = comparison.originalData.totals ?? comparison.originalData.total;
    next.total = next.totals; next.clientRatePending = false;
  }
  if (changed.includes('quantity')) next.availableQuantity = Number(comparison.originalData?.availableQuantity || 0) + Number(next.quantity) - Number(comparison.originalData?.quantity || 0);
  next.clientRequestDraftDecisions = [...(row.clientRequestDraftDecisions || []).filter(item => !changed.includes(item.field)),
    ...changed.map(field => ({ historyShiftId: Number(comparison.historyData.id), field, decision,
      originalValue: comparison.changes[field].base, requestedValue: comparison.changes[field].requested, appliedValue: next[field] }))];
  return next;
}
export { draftClientCellDecision, clientRequestWorksheetRows };

/** Saving an adjusted requested field records that review without changing other cells. */
export function reviewDocumentAdjustment(row: Row, values: Row, comparison: any) {
  const next = { ...row, ...values };
  const changed = Object.keys(comparison?.changes || {}).filter(field => normalized(field, next[field]) !== normalized(field, row[field]));
  next.clientRequestDraftDecisions = [...(row.clientRequestDraftDecisions || []).filter(item => !changed.includes(item.field)),
    ...changed.map(field => ({ historyShiftId: Number(comparison.historyData.id), field,
      decision: normalized(field, next[field]) === normalized(field, comparison.changes[field].base) ? 'reject' : 'accept',
      originalValue: comparison.changes[field].base, requestedValue: comparison.changes[field].requested, appliedValue: next[field] }))];
  return next;
}

export function documentRequestHistory(history: any) {
  let metadata: any = {};
  try { metadata = JSON.parse(history?.adminNotes || '{}'); } catch { return history; }
  const proposal = metadata.documentRequestProposal;
  if (!proposal) return history;
  const raw = [...(proposal.updatedShifts || proposal.updatedActualsShifts || []), ...(proposal.newShifts || proposal.newActualsShifts || [])];
  const rows = [...(history.shiftsHistory || [])].sort((a, b) => a.id - b.id);
  return { ...history, shiftsHistory: rows.map((row, index) => {
    const proposalRow = metadata.documentRequestRowsByHistoryId?.[String(row.id)] || raw[index];
    return { ...row, ...proposalRow, id: row.id,
      ...(proposalRow?.rate === null || proposalRow?.rate === '' ? { rate: null, total: null, totals: null, clientRatePending: true } : {}) };
  }) };
}

export function documentPositionSelection(row: Row, position: string) {
  const selected = clientPositionSelection(row, position);
  return hasPendingClientRate(selected) ? { ...selected, rate: null, total: null, totals: null, clientRatePending: true } : selected;
}

/** Reopen the latest submitted request using document-row ids, never private Admin edits. */
export function clientDocumentRequestView(document: Row, history: Row | null, rowKey: 'actualsShifts' | 'invoiceShifts') {
  if (document.status !== 'request' || history?.status !== 'request') return document;
  let metadata: Row = {};
  try { metadata = JSON.parse(history.adminNotes || '{}'); } catch { return document; }
  if (metadata.documentId != null && Number(metadata.documentId) !== Number(document.id)) return document;
  const sentAt = document.actualSent || document.invoiceSent;
  if (sentAt && new Date(history.createdAt).getTime() < new Date(sentAt).getTime()) return document;
  const proposal = metadata.documentRequestProposal;
  if (!proposal) return document;
  const raw = [...(proposal.updatedShifts || proposal.updatedActualsShifts || []), ...(proposal.newShifts || proposal.newActualsShifts || [])];
  const rows = [...(history.shiftsHistory || [])].sort((a, b) => a.id - b.id).map((row, index) => {
    const requested = metadata.documentRequestRowsByHistoryId?.[String(row.id)] || raw[index];
    const original = (document[rowKey] || []).find(item => String(item.id) === String(requested?.id));
    return { ...row, ...requested, id: requested?.id ?? original?.id ?? row.id,
      ...(requested?.rate === null || requested?.rate === '' ? { rate: null, total: null, totals: null, clientRatePending: true } : {}) };
  });
  return { ...document, [rowKey]: rows,
    ...Object.fromEntries(['venue', 'address', 'latitude', 'longitude', 'clientPo'].filter(key => proposal[key] !== undefined).map(key => [key, proposal[key]])) };
}
